import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';
import type { VehicleType, VehicleOwner, VehicleCost, VehicleValuation } from '../types/Vehicle';
import type { ErrorField } from '../types/ErrorField';

export default function useVehiclePageForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
    const [isCreating, setIsCreating] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    // Vehicle fields
    const [name, setName] = useState('');
    const [vehicleType, setVehicleType] = useState<VehicleType>('CAR');
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [chassis, setChassis] = useState('');
    const [renavam, setRenavam] = useState('');
    const [plate, setPlate] = useState('');
    const [yearManufacture, setYearManufacture] = useState<number>(new Date().getFullYear());
    const [yearModel, setYearModel] = useState<number>(new Date().getFullYear());
    const [color, setColor] = useState('');
    const [plateState, setPlateState] = useState('');
    const [comments, setComments] = useState('');

    // Obligation (required)
    const [creditOperationId, setCreditOperationId] = useState('');
    const [obligations, setObligations] = useState<any[]>([]);
    const [isLoadingObligations, setIsLoadingObligations] = useState(false);

    // Owner (person search)
    const [owner, setOwner] = useState<VehicleOwner>({ personId: '', name: '', document: '' });
    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerResults, setOwnerResults] = useState<any[]>([]);
    const [isSearchingOwner, setIsSearchingOwner] = useState(false);
    const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

    // Valuation (optional)
    const [valuation, setValuation] = useState<VehicleValuation | null>(null);

    // Costs (optional)
    const [costs, setCosts] = useState<VehicleCost[]>([]);

    const addCost = (cost: VehicleCost) => setCosts(prev => [...prev, cost]);
    const removeCost = (index: number) => setCosts(prev => prev.filter((_, i) => i !== index));

    // Load obligations on mount
    useEffect(() => {
        const loadObligations = async () => {
            setIsLoadingObligations(true);
            try {
                const result = await collateralApi.getObligations({ page: 1, pageSize: 500 });
                setObligations(result.items || []);
            } catch {
                setObligations([]);
            } finally {
                setIsLoadingObligations(false);
            }
        };
        loadObligations();
    }, []);

    const searchOwner = async (query: string) => {
        if (!query || query.length < 3) {
            setOwnerResults([]);
            setShowOwnerDropdown(false);
            return;
        }
        setIsSearchingOwner(true);
        try {
            const [legalResults, individualResults] = await Promise.all([
                collateralApi.searchLegalPerson(query).catch(() => []),
                collateralApi.searchPerson(query).catch(() => []),
            ]);

            const normalized = [
                ...legalResults.map((p: any) => ({
                    personId: p.personId || p.uuid || p.id || '',
                    name: p.legalName || p.fantasyName || p.name || '',
                    document: (p.govTaxId || '').replace(/\D/g, ''),
                    documentFormatted: p.govTaxIdFormatted || p.govTaxId || '',
                    type: 'PJ',
                })),
                ...individualResults.map((p: any) => ({
                    personId: p.id || p.personId || '',
                    name: p.name || p.fullName || '',
                    document: (p.socialId || '').replace(/\D/g, ''),
                    documentFormatted: p.socialId || '',
                    type: 'PF',
                })),
            ];

            setOwnerResults(normalized);
            setShowOwnerDropdown(normalized.length > 0);
        } catch {
            setOwnerResults([]);
        } finally {
            setIsSearchingOwner(false);
        }
    };

    const handleSelectOwner = (person: any) => {
        setOwner({
            personId: person.personId,
            name: person.name,
            document: person.document,
        });
        setOwnerSearch(person.name);
        setShowOwnerDropdown(false);
    };

    const handleCreate = async () => {
        setIsCreating(true);
        setErrorFields([]);
        try {
            await collateralApi.createVehicle({
                name: name || `${brand} ${model} ${yearModel}`,
                vehicle_type: vehicleType,
                brand,
                model,
                chassis: chassis.toUpperCase(),
                renavam,
                plate: plate.toUpperCase(),
                year_manufacture: yearManufacture,
                year_model: yearModel,
                color: color || undefined,
                plate_state: plateState.toUpperCase(),
                comments: comments || undefined,
                credit_operation_id: creditOperationId,
                owner: {
                    person_id: owner.personId,
                    name: owner.name,
                    document: owner.document,
                },
                valuation: valuation ? {
                    market_value: valuation.marketValue,
                    eligible_value: valuation.eligibleValue,
                    monthly_rate: valuation.monthlyRate,
                    source_name: valuation.sourceName,
                    valuation_date: valuation.valuationDate,
                } : undefined,
                costs: costs.map(c => ({
                    cost_type: c.costType,
                    value: c.value,
                    cost_date: c.costDate,
                    registration_number: c.registrationNumber,
                    registration_date: c.registrationDate,
                    description: c.description,
                })),
            });
            onCreated();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Há campos obrigatórios!');
                setErrorFields(e.data.errors);
            } else if (e instanceof ApiError && e.status === 409) {
                toast.error(e.data?.message || e.message);
            } else {
                toast.error(e?.message || 'Erro ao cadastrar veículo');
            }
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isCreating, errorFields,
        name, setName,
        vehicleType, setVehicleType,
        brand, setBrand,
        model, setModel,
        chassis, setChassis,
        renavam, setRenavam,
        plate, setPlate,
        yearManufacture, setYearManufacture,
        yearModel, setYearModel,
        color, setColor,
        plateState, setPlateState,
        comments, setComments,
        creditOperationId, setCreditOperationId,
        obligations, isLoadingObligations,
        owner, setOwner,
        ownerSearch, setOwnerSearch,
        ownerResults, isSearchingOwner, showOwnerDropdown,
        searchOwner, handleSelectOwner,
        valuation, setValuation,
        costs, addCost, removeCost,
        handleCreate,
    };
}
